import React, {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Moment } from "moment";
import { useBoolean, useRouter } from "@miplatform/hooks";
import {
  CalendarEventType,
  CalendarType,
  HubStatuses,
  IApiResponse,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IAttemptTrayConfig,
  IAutoAssignWorkOrderTemplateStatus,
  ICalendarConfig,
  IDateRangeISO,
  ITenantHolidayResponse,
  IUserListItemResponse,
} from "@miplatform/types";
import { useSignalR } from "@mibuildweb/hooks";
import { loadCalendarEvents } from "./utils/loadCalendarEvents";
import { CalendarSetsConfigDataItem } from "../../stores/calendarsStore/calendarsStore.types";
import { HoldAttemptFormData } from "../../views/NewHomeConstruction/NhcCalendar.types";
import { SchedulerViewType } from "./CalendarProvider.types";
import { useCalendarProviderViewModel } from "./useCalendarProviderViewModel";

const CalendarContext = createContext<CalendarContextType | undefined>(
  undefined
);

export const CalendarProvider: React.FC<CalendarProviderProps> = ({
  children,
}) => {
  const { params } = useRouter();

  const { calendarSetId, calendarId: initialCalendarId } = params;

  const [selectedUser, setSelectedUser] =
    useState<IUserListItemResponse | null>(null);

  const handleSelectUser = useCallback((user: IUserListItemResponse | null) => {
    setSelectedUser(user);
  }, []);
  const [
    isCreateWorkOrderDrawerOpen,
    handleCreateWorkOrderDrawerOpen,
    handleCreateWorkOrderDrawerClose,
  ] = useBoolean();

  const {
    events,
    zoom,
    assignableUsers,
    schedulerUsers,
    activeCalendarId,
    calendarSet,
    calendarConfig,
    date,
    dateRange,
    search,
    debouncedSearch,
    trayBuckets,
    tenantHolidays,
    users,
    highlightedAttempts,
    loadUsers,
    isLoading: isCalendarLoading,
    selectedAttempt,
    setCalendarDate,
    onDateRangeChange: setDateRange,
    setZoom,
    getCalendarCounts,
    updateAttempt,
    highlightAttempts,
    unhighlightAttempts,
    clearWorkOrderSchedule,
    unscheduleAttempt,
    handleLoadEvents,
    onAttemptsUpdate,
    handleCalendarSearch,
    getTrayDataForCalendar,
    getTenantHolidays,
    resetTrays,
    calendarCounts,
    resetSearch,
    setSelectedAttempt,
    setVerticalTabActiveFilter,
    verticalTabActiveFilter,
    activeViewRef,
    setActiveView,
    handleForceToNextAttempt,
    startWorkOrder,
    submitWorkOrder,
    closeWorkOrder,
    holdWorkOrder,
    resetWorkOrder,
    setWorkOrderLock,
    assigneeModeUser,
    isScheduler,
  } = useCalendarProviderViewModel({
    calendarSetId,
    calendarId: initialCalendarId,
    selectedUser,
  });

  const assignableUserIdsKey = useMemo(
    () =>
      (assignableUsers ?? [])
        .map((user: IUserListItemResponse) => user.id)
        .join(","),
    [assignableUsers]
  );

  const assignableUserIds = useMemo(
    () => (assignableUserIdsKey ? assignableUserIdsKey.split(",") : []),
    [assignableUserIdsKey]
  );

  const eventsList = useMemo(() => Object.values(events), [events]);

  const [autoAssignStatusUpdates, setAutoAssignStatusUpdates] = useState<
    IAutoAssignWorkOrderTemplateStatus[]
  >([]);

  const canLoadEvents =
    !!calendarSet?.config &&
    !!calendarConfig &&
    !(
      calendarSet.config.eventType === CalendarEventType.WorkOrder &&
      (calendarConfig.calendarType === CalendarType.AutoHold ||
        calendarConfig.calendarType === CalendarType.HoldsList)
    );

  const handleAutoAssignStatusUpdate = (
    updates: IAutoAssignWorkOrderTemplateStatus[]
  ) => {
    setAutoAssignStatusUpdates(updates);

    const isForThisCalendar = updates.some(
      (update) => update?.calendarSetId === calendarSetId
    );
    if (
      !isForThisCalendar ||
      calendarSet?.config.eventType !== CalendarEventType.WorkOrder
    ) {
      return;
    }

    if (canLoadEvents) {
      handleLoadEvents();
    }
    getCalendarCounts();
    loadUsers();
  };

  const {
    isLoading: isSignalRLoading,
    connectionStatus,
    start,
    stop,
  } = useSignalR({
    calendarSetId: calendarSetId ?? null,
    assignableUserIds,
    onAttemptsUpdate,
    onAutoAssignStatusUpdate: handleAutoAssignStatusUpdate,
  });

  const value: CalendarContextType = useMemo(
    () => ({
      zoom,
      date,
      dateRange,
      events: eventsList,
      calendarSet: calendarSet ?? null,
      calendarConfig,
      activeCalendarId,
      assignableUsers,
      schedulerUsers,
      connectionStatus,
      autoAssignStatusUpdates,
      isCalendarLoading: isCalendarLoading || isSignalRLoading,
      search,
      debouncedSearch,
      selectedUser,
      trayBuckets,
      selectedAttempt,
      calendarCounts,
      tenantHolidays,
      users,
      isAssigneeMode: !!assigneeModeUser,
      assigneeModeUser,
      highlightedAttempts,
      isCreateWorkOrderDrawerOpen,
      handleCreateWorkOrderDrawerOpen,
      handleCreateWorkOrderDrawerClose,
      getCalendarCounts,
      updateAttempt,
      highlightAttempts,
      unhighlightAttempts,
      loadUsers,
      clearWorkOrderSchedule,
      holdWorkOrder,
      startWorkOrder,
      submitWorkOrder,
      closeWorkOrder,
      resetWorkOrder,
      setWorkOrderLock,
      unscheduleAttempt,
      setSelectedAttempt,
      setZoom,
      setCalendarDate,
      setDateRange,
      handleCalendarSearch,
      handleSelectUser,
      resetSearch,
      refreshData: handleLoadEvents,
      getTrayDataForCalendar,
      setVerticalTabActiveFilter,
      verticalTabActiveFilter,
      activeViewRef,
      setActiveView,
      handleForceToNextAttempt,
      isScheduler,
    }),
    [
      zoom,
      calendarConfig,
      calendarSet,
      activeCalendarId,
      assignableUsers,
      schedulerUsers,
      isCalendarLoading,
      isSignalRLoading,
      connectionStatus,
      autoAssignStatusUpdates,
      tenantHolidays,
      eventsList,
      date,
      dateRange,
      search,
      debouncedSearch,
      selectedUser,
      trayBuckets,
      selectedAttempt,
      calendarCounts,
      verticalTabActiveFilter,
      users,
      assigneeModeUser,
      highlightedAttempts,
      isCreateWorkOrderDrawerOpen,
      highlightAttempts,
      unhighlightAttempts,
      resetSearch,
      holdWorkOrder,
      unscheduleAttempt,
      clearWorkOrderSchedule,
      loadUsers,
      loadCalendarEvents,
      getCalendarCounts,
      handleSelectUser,
      startWorkOrder,
      submitWorkOrder,
      closeWorkOrder,
      resetWorkOrder,
      setWorkOrderLock,
      isScheduler,
    ]
  );

  useEffect(() => {
    if (!calendarSetId) {
      return undefined;
    }
    start();
    // Always tear down on cleanup. Previously this was guarded by
    // `connectionStatus === Connected`, but `connectionStatus` was not part of
    // the dependency array, so the cleanup almost always saw the initial
    // `Disconnected` value and skipped stop(), leaking the SignalR hub and its
    // subscriptions across re-runs and on unmount.
    return () => {
      stop();
    };
  }, [calendarSetId, assignableUserIdsKey, start, stop]);

  useEffect(() => {
    if (canLoadEvents) {
      handleLoadEvents();
    }
  }, [canLoadEvents, handleLoadEvents]);

  useEffect(() => {
    getCalendarCounts();
  }, [getCalendarCounts]);

  useEffect(() => {
    loadUsers();
    setSelectedAttempt(null);
    handleCreateWorkOrderDrawerClose();
  }, [calendarConfig, date, selectedUser, loadUsers]);

  useEffect(() => {
    getTenantHolidays();
  }, []);

  useEffect(() => {
    // TODO: Remove this logic check wehn trays are implemented nicely for other calendar types
    if (calendarSet?.config.eventType === CalendarEventType.Order) {
      getTrayDataForCalendar();
    }

    return () => {
      resetTrays();
    };
  }, [calendarSet?.config.id]);

  // TODO: Refreshing trays re-renders everything and that will include losing the tooltips and actions while using the calendar. Something like this should be within the trays directly if possible
  // useEffect(() => {
  //   if (calendarSet) {
  //     timerRef.current = setInterval(() => {
  //       getTrayDataForCalendar();
  //     }, getTotalMilliseconds(calendarSet.config.pollingPeriodSeconds));
  //   }

  //   return () => {
  //     if (timerRef.current) {
  //       clearInterval(timerRef.current);
  //       timerRef.current = null;
  //     }
  //     resetTrays();
  //   };
  // }, [calendarSet?.config.id]);

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = (): CalendarContextType => {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error("useCalendarContext must be used within CalendarProvider");
  }
  return context;
};

interface CalendarContextType {
  isAssigneeMode: boolean;
  isScheduler: boolean;
  date: Moment;
  dateRange: IDateRangeISO;
  setCalendarDate: (newDate: Date | string | Moment) => void;
  setDateRange: (newRange: IDateRangeISO) => void;
  zoom: number;
  setZoom: (zoom: number) => void;
  events: IAttempt[];
  activeCalendarId?: string;
  calendarCounts: Record<string, string>;
  calendarSet: CalendarSetsConfigDataItem | null;
  calendarConfig: ICalendarConfig | null;
  assignableUsers?: IUserListItemResponse[];
  schedulerUsers?: IUserListItemResponse[];
  connectionStatus: HubStatuses;
  autoAssignStatusUpdates: IAutoAssignWorkOrderTemplateStatus[];
  isCalendarLoading: boolean;
  search: string;
  debouncedSearch: string;
  users: IUserListItemResponse[];
  highlightedAttempts: Record<string, boolean>;
  tenantHolidays: ITenantHolidayResponse[];
  loadUsers: () => void;
  handleCalendarSearch: (value: string) => void;
  handleSelectUser: (user: IUserListItemResponse | null) => void;
  selectedUser: IUserListItemResponse | null;
  refreshData: () => void;
  trayBuckets: IAttemptTrayConfig[];
  selectedAttempt: IAttempt | null;
  assigneeModeUser?: IUserListItemResponse | null;
  isCreateWorkOrderDrawerOpen: boolean;
  handleCreateWorkOrderDrawerOpen: () => void;
  handleCreateWorkOrderDrawerClose: () => void;
  updateAttempt: (attempt?: IAttempt) => void;
  startWorkOrder: (attempt: IAttempt) => Promise<IAttempt | null>;
  submitWorkOrder: (attempt: IAttempt) => Promise<IAttempt | null>;
  closeWorkOrder: (attempt: IAttempt) => Promise<IApiResponse<IAttempt>>;
  highlightAttempts: (eventId: string | string[]) => void;
  unhighlightAttempts: (eventId: string | string[]) => void;
  holdWorkOrder: (data: HoldAttemptFormData) => Promise<IApiResponse<IAttempt>>;
  resetWorkOrder: (attempt: IAttempt, onSuccess?: () => void) => void;
  setWorkOrderLock: (attempt: IAttempt, isLocked: boolean) => void;
  setSelectedAttempt: (attempt: IAttempt | null) => void;
  resetSearch: () => void;
  clearWorkOrderSchedule: (user: IUserListItemResponse) => void;
  getTrayDataForCalendar: () => Promise<void>;
  setVerticalTabActiveFilter: (filter: string | null) => void;
  unscheduleAttempt: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  getCalendarCounts: () => void;
  verticalTabActiveFilter: string | null;
  activeViewRef: RefObject<SchedulerViewType | null>;
  setActiveView: (view: SchedulerViewType) => void;
  handleForceToNextAttempt: (
    attempt: IAttemptListItemResponseForWorkOrder,
    assigneeId?: string
  ) => void;
}
interface CalendarProviderProps {
  children: ReactNode;
}
